import React, {useEffect, useRef,useState} from 'react';
import {
  ActivityIndicator,
  Image,
  Linking,
  Platform,
  StyleSheet,
  View,
} from 'react-native';
import {KeyboardAwareScrollView} from 'react-native-keyboard-aware-scroll-view';
import {Formik} from 'formik';
import {observer} from 'mobx-react';

import Button from '../../components/ui-controls/Button';
import {ErrorDialog} from '../../components/ui-controls/ErrorDialog';
import TextInput from '../../components/ui-controls/form/TextInput';
import ContentView from '../../components/ui-controls/layout/ContentView';
import Text from '../../components/ui-controls/Text';
import {Title} from '../../components/ui-controls/text/Title';
import TmpDatePicker from '../../components/ui-controls/TmpDatePicker';
import config from '../../config';
import {useWebScreenDimensions} from '../../hooks/useWebScreenDimensions';
import BannerImg from '../../images/brand/giftCardsShop/banner.png';
import {AppRoute} from '../../models/AppRoute';
import {useStores} from '../../stores/use-stores';
import {OrderLine} from '../../transport/apiServiceResponse';
import {getTodayAt0Hours} from '../../utils/date';
import {makeGiftCardRecipientData} from '../../utils/giftCards';
import {getApiErrorMessage} from '../../utils/mfl';
import CardSelector from '../components/CardSelector';
import {MAX_CONTENT_WIDTH, MAX_FORM_WIDTH} from '../constants';
import {GiftCardShopRouteNavigatorScreenProps} from '../types/navigationTypes';

import {GiftCardData,GiftCardDataSchema} from './schema';

const defaultValues: GiftCardData = {
  note: '',
  recipientName: '',
  recipientEmail: '',
  recipientPhoneNumber: '',
  deliveryDate: getTodayAt0Hours(),
};

const makeOrderLines = (products: OrderLine[], selectedProduct: OrderLine) => {
  const selectedProductQty1 = {
    product_id: selectedProduct.product_id,
    quantity: 1,
  };
  // We don't have a "Cart View", so in order to prevent issues when using
  // the "gift card shop" in another device, we 0 the other available products
  const restOfTheProductsQty0 = products
    .filter(orderLine => orderLine.product_id !== selectedProduct.product_id)
    .map(orderLine => ({
      product_id: orderLine.product_id,
      quantity: 0,
    }));
  const orderLines = [selectedProductQty1, ...restOfTheProductsQty0];
  return orderLines;
};

const Link = ({url, urlText}: {url: string; urlText: string}) => (
  <Text style={{color: 'blue'}} onPress={() => Linking.openURL(url)}>
    {urlText}
  </Text>
);

const TopSection = () => (
  <>
    <Image source={BannerImg} style={styles.bannerImage} />
    <Text variant="body" style={styles.topSectionCopy}>
      The {config.APP_NAME} Digital Gift Card is sent to your recipient by email
      and can be redeemed online at any {config.APP_NAME} online store. Visit{' '}
      <Link
        urlText={`www.${config.HOST_DOMAIN}`}
        url={`http://${config.HOST_DOMAIN}`}
      />{' '}
      or download our apps to find your store.
    </Text>
  </>
);

export const GiftCardStoreShopScreen = observer(
  ({
    navigation,
    route,
  }: GiftCardShopRouteNavigatorScreenProps<AppRoute.GIFT_CARDS_HOME>) => {
    const isResetForm = route?.params?.isResetForm;

    const {cartStore, rangeStore} = useStores();
    const {isWebDesktopScreen} = useWebScreenDimensions();

    const [selectedProduct, setSelectedProduct] = useState<OrderLine | null>(
      null,
    );
    const [isUpdatingRecipientData, setUpdatingRecipientData] = useState(false);
    const [submitErrorMessage, setSubmitErrorMessage] = useState('');
    const [isNativeDatePickerModalOpen, setNativeDatePickerModalOpen] =
      useState(false);

    const formikRef = useRef<typeof Formik>();
    useEffect(() => {
      if (isResetForm) {
        formikRef.current?.resetForm({values: defaultValues});
        setSelectedProduct(null);
        navigation.setParams({isResetForm: false});
      }
    }, [isResetForm, navigation]);

    // David: No need to load the existing order/cart here. Just start every
    // time from scratch (that is don't wait on `cartStore.isLoadingOrder` and
    // don't use `cartStore?.currentOrder?.order_lines?` to pre-select a
    // product)
    const isLoading = rangeStore.isLoadingDepartmentLines;

    const noteInput = useRef<typeof TextInput>(null);
    const emailInput = useRef<typeof TextInput>(null);
    const phoneInput = useRef<typeof TextInput>(null);

    const handleSelectProduct = async (product: OrderLine) =>
      setSelectedProduct(product);

    const handleFormSubmit = async (
      values: GiftCardData,
      // actions: FormikHelpers<GiftCardData>,
    ) => {
      setUpdatingRecipientData(true);
      try {
        if (!selectedProduct) throw new Error('Please select a product');

        const orderLines = makeOrderLines(products, selectedProduct);
        const giftCardRecipientData = makeGiftCardRecipientData(values);

        await cartStore.updateOrderDirectly(orderLines);
        await cartStore.updateGiftCardRecipientDetails(giftCardRecipientData);

        cartStore.setGiftCardRecipientFormValues(values);
        navigation.navigate(AppRoute.GIFT_CARDS_STORE_REVIEW);
      } catch (error) {
        console.error(error);
        const errorMessage = getApiErrorMessage(error);
        setSubmitErrorMessage(errorMessage);
      } finally {
        setUpdatingRecipientData(false);
      }
    };

    const handleErrorDialogOk = () => setSubmitErrorMessage('');

    const products = rangeStore?.departmentLines || [];

    if (isLoading || !products.length) {
      return (
        <ContentView>
          <TopSection />
          <ActivityIndicator style={styles.activityIndicator} />
        </ContentView>
      );
    }

    const desktopDependentStyles =
      getDesktopDependentStyles(isWebDesktopScreen);

    return (
      <ContentView style={{maxWidth: MAX_CONTENT_WIDTH}}>
        <KeyboardAwareScrollView>
          <TopSection />
          <View style={styles.cardSelector}>
            <Title>1. CHOOSE YOUR GIFT CARD VALUE</Title>
            <CardSelector
              products={products}
              selectedProduct={selectedProduct}
              onSelectProduct={handleSelectProduct}
            />
          </View>
          {!!selectedProduct && (
            <Formik<GiftCardData>
              initialValues={defaultValues}
              onSubmit={handleFormSubmit}
              validationSchema={GiftCardDataSchema}
              innerRef={formikRef}>
              {({
                errors,
                handleBlur,
                handleChange,
                handleSubmit,
                setFieldValue,
                touched,
                values,
              }) => (
                <>
                  <Title>2. PERSONALISE YOUR GIFT</Title>
                  <View style={desktopDependentStyles.formView}>
                    <View style={desktopDependentStyles.centeredFormUiElements}>
                      <TextInput
                        accessibilityLabel="To"
                        autoCapitalize="words"
                        autoComplete="name-given"
                        autoFocus={true}
                        errorMessageWhenTouched={errors.recipientName}
                        keyboardType="default"
                        label="To"
                        maxLength={100}
                        onBlur={handleBlur('recipientName')}
                        onChangeText={handleChange('recipientName')}
                        onSubmitEditing={() => noteInput.current?.focus()}
                        placeholder="Recipient Name"
                        touched={touched.recipientName}
                        value={values.recipientName}
                      />
                      <TextInput
                        accessibilityLabel="Message"
                        errorMessageWhenTouched={errors.note}
                        inputStyle={
                          Platform.OS === 'web' ? {resize: 'auto'} : undefined
                        }
                        keyboardType="default"
                        label="Message"
                        maxLength={10000}
                        multiline={true}
                        numberOfLines={Platform.OS === 'web' ? 6 : 2}
                        onBlur={handleBlur('note')}
                        onChangeText={handleChange('note')}
                        onSubmitEditing={() => emailInput.current?.focus()}
                        placeholder="Add your message..."
                        ref={noteInput}
                        touched={touched.note}
                        value={values.note}
                      />
                    </View>
                  </View>
                  <Title>3. DELIVERY DETAILS</Title>
                  <Text variant="body" style={{marginBottom: 16}}>
                    Your Gift Card will be delivered to the recipient by email
                    on the selected delivery date (emails are sent from 7AM AEST
                    on the selected date). If you choose today&apos;s date, the
                    Gift Card will be sent immediately.
                  </Text>
                  <View style={desktopDependentStyles.formView}>
                    <View style={desktopDependentStyles.centeredFormUiElements}>
                      <TextInput
                        accessibilityLabel="Email"
                        autoCapitalize="none"
                        autoComplete="email"
                        errorMessageWhenTouched={errors.recipientEmail}
                        keyboardType="email-address"
                        label="Email"
                        maxLength={100}
                        onBlur={handleBlur('recipientEmail')}
                        onChangeText={handleChange('recipientEmail')}
                        onSubmitEditing={() => phoneInput.current?.focus()}
                        placeholder="Recipient Email"
                        ref={emailInput}
                        touched={touched.recipientEmail}
                        value={values.recipientEmail}
                      />
                      <TextInput
                        accessibilityLabel="Mobile Number"
                        autoCapitalize="none"
                        autoComplete="tel"
                        errorMessageWhenTouched={errors.recipientPhoneNumber}
                        keyboardType="phone-pad"
                        label="Mobile Number"
                        maxLength={20}
                        onBlur={handleBlur('recipientPhoneNumber')}
                        onChangeText={handleChange('recipientPhoneNumber')}
                        placeholder="Recipient Mobile Number"
                        ref={phoneInput}
                        touched={touched.recipientPhoneNumber}
                        value={values.recipientPhoneNumber}
                      />
                      <TmpDatePicker
                        label="Delivery Date"
                        value={values.deliveryDate}
                        onChange={(date: any) => {
                          setNativeDatePickerModalOpen(false);
                          setFieldValue('deliveryDate', date);
                        }}
                        onNativePressIn={() =>
                          setNativeDatePickerModalOpen(true)
                        }
                        nativeText={
                          values.deliveryDate?.toLocaleDateString('en-AU') ||
                          'Delivery date'
                        }
                        isNativeDialogOpen={isNativeDatePickerModalOpen}
                        onCancel={() => setNativeDatePickerModalOpen(false)}
                        error={errors.deliveryDate}
                        nativeMinimumDate={getTodayAt0Hours()}
                      />
                    </View>
                  </View>
                  <Button
                    onPress={handleSubmit}
                    title="NEXT"
                    containerStyle={styles.nextButton}
                    loading={isUpdatingRecipientData}
                    disabled={isUpdatingRecipientData}
                  />
                </>
              )}
            </Formik>
          )}
          <ErrorDialog
            isVisible={!!submitErrorMessage}
            onOk={handleErrorDialogOk}
            message={submitErrorMessage}
          />
        </KeyboardAwareScrollView>
      </ContentView>
    );
  },
);

export default GiftCardStoreShopScreen;

// Ugly
const getDesktopDependentStyles = (isWebDesktopScreen: boolean) =>
  isWebDesktopScreen
    ? {
        formView: {
          alignItems: 'center',
          paddingHorizontal: 8,
        },
        centeredFormUiElements: {
          width: MAX_FORM_WIDTH,
        },
      }
    : {
        formView: {
          paddingHorizontal: 8,
        },
        centeredFormUiElements: {},
      };

const styles = StyleSheet.create({
  bannerImage: {
    width: '100%',
    height: undefined,
    aspectRatio: 3 / 2,
  },
  topSectionCopy: {
    marginTop: 16,
    marginBottom: 8,
  },
  activityIndicator: {
    marginTop: 16,
  },
  cardSelector: {
    width: '100%',
    marginBottom: 16,
  },
  nextButton: {
    marginTop: 8,
    marginBottom: 16,
  },
});
